import type { Locale, Messages } from "@/locale";
import { statusLabel } from "@/locale";

export function formatBytes(value: number | null | undefined, locale: Locale = "en"): string {
  if (value == null || value < 0) return "—";
  const gb = value / 1024 ** 3;
  if (gb >= 1) {
    return new Intl.NumberFormat(locale, {
      style: "unit",
      unit: "gigabyte",
      unitDisplay: "short",
      maximumFractionDigits: gb >= 10 ? 0 : 1,
    }).format(gb);
  }
  const mb = value / 1024 ** 2;
  return new Intl.NumberFormat(locale, {
    style: "unit",
    unit: "megabyte",
    unitDisplay: "short",
    maximumFractionDigits: 0,
  }).format(mb);
}

export function formatWhen(iso: string | null | undefined, locale: Locale = "en"): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return (
    new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "UTC",
    }).format(d) + " UTC"
  );
}

export function formatRelative(
  iso: string | null | undefined,
  locale: Locale = "en",
  now = Date.now(),
): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const sec = Math.round((now - t) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (Math.abs(sec) < 60) return rtf.format(-sec, "second");
  const min = Math.round(sec / 60);
  if (Math.abs(min) < 60) return rtf.format(-min, "minute");
  const hr = Math.round(min / 60);
  if (Math.abs(hr) < 48) return rtf.format(-hr, "hour");
  const day = Math.round(hr / 24);
  if (Math.abs(day) < 14) return rtf.format(-day, "day");
  return formatWhen(iso, locale);
}

export function shortId(id: string, keep = 8): string {
  if (id.length <= keep + 1) return id;
  return `${id.slice(0, keep)}…`;
}

export function lifecycleLabel(m: Messages, value: string): string {
  return statusLabel(m, value);
}

export function roleLabel(m: Messages, role: string): string {
  return m.account.roles[role] ?? m.account.roleUnknown;
}

export function auditActionLabel(m: Messages, action: string): string {
  return m.account.auditActions[action] ?? m.account.auditUnknown;
}

/** Demo machines show a localized semantic name; user/operator names stay as stored. */
export function machineDisplayName(
  m: Messages,
  machine: { display_name: string; mode: string },
): string {
  if (machine.mode === "demo") return m.machines.demoName;
  return machine.display_name;
}
